import React, {useMemo} from 'react';
import {Button, Typography, Card, Space, Alert, Spin, Select} from 'antd';
import {
  CheckCircleOutlined,
  LinkOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  NodeIndexOutlined,
} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {ModelFlatNode} from '../../../../store/modelLinks';
import {ModelTreeNode} from '../../../../store/modelFile';
import {canBeParentOfModel, isModelExternal, isStaticMetadataValid} from '../../../../store/model';
import {calculateBranchSizeFromModelUuid} from '../../../../store/modelLinks/utils';
import {useModelHierarchyRootsByMetadata} from '../../../../store/modelLinks';
import {tid} from '../../../../testUtils';
import styles from './LineageSelectionAntd.module.scss';

const {Text} = Typography;

interface LineageSelectionAntdProps {
  model: ModelDTO;
  flatHierarchy: ModelFlatNode[];
  files: ModelTreeNode[];
  onParentSelect: (node: ModelFlatNode<ModelDTO>) => void;
  onClearParent: () => void;
  onNewHierarchy: () => void;
  onRootSelect: (rootId: string) => void;
  currentMode: 'parent' | 'root';
  onModeChange: (mode: 'parent' | 'root') => void;
  isModelInHierarchy: boolean;
  submitClicked: boolean;
}

const LineageSelectionAntd: React.FC<LineageSelectionAntdProps> = ({
  model,
  flatHierarchy,
  onParentSelect,
  onClearParent,
  onNewHierarchy,
  onRootSelect,
  currentMode,
  onModeChange,
  isModelInHierarchy,
  submitClicked,
}) => {
  //const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const roots = useModelHierarchyRootsByMetadata(model);
  const externalModel = isModelExternal(model);
  const validMetadata = useMemo(() => isStaticMetadataValid(model as any), [model]);

  const currentNode = flatHierarchy.find((n) => n.model.uuid === model.uuid);
  const getModelName = (inputModel: any) => inputModel?.name || inputModel?.metadata?.name;
  const possibleParentsCount = flatHierarchy.length - calculateBranchSizeFromModelUuid(flatHierarchy, model.uuid);
  const parentSelectionMessage = useMemo(() => {
    if (isModelInHierarchy && possibleParentsCount === 1) {
      return 'The only one possible parent has been selected.';
    }

    if (possibleParentsCount > 1 || (possibleParentsCount > 0 && !isModelInHierarchy)) {
      return 'Please select parent model entry. Your new entry will be linked to this lineage.';
    }

    if (possibleParentsCount === 0 && !isModelInHierarchy) {
      return 'There is no parent you can select. This model entry is at top level.';
    }

    return null;
  }, [isModelInHierarchy, possibleParentsCount]);

  const selectableParents = useMemo(
    () =>
      flatHierarchy.filter(
        (item) =>
          item.model.uuid !== model.uuid &&
          (canBeParentOfModel(item.model, model) || currentNode?.originalParentId === item.model.uuid),
      ),
    [flatHierarchy, model, currentNode?.originalParentId],
  );

  const selectedParent = useMemo(
    () => selectableParents.find((item) => item.model.uuid === currentNode?.parentId),
    [selectableParents, currentNode?.parentId],
  );

  // Render Parent Selection Mode
  const renderParentSelection = () => (
    <div className={styles.parentSelection}>
      <Space direction="vertical" size={0} style={{width: '100%'}}>
        {parentSelectionMessage && <div className={styles.parentSelectionMessage}>{parentSelectionMessage}</div>}

        <Select
          className={styles.parentSelect}
          placeholder="Select Parent Entry"
          value={selectedParent?.model.uuid}
          notFoundContent="No matching published parent models found."
          allowClear
          onChange={(parentId) => {
            // If the selected value is undefined/null, user clicked clear button or unselected
            if (!parentId) {
              onClearParent();
              return;
            }

            // If clicking on the already selected parent, toggle (deselect) it
            if (selectedParent?.model.uuid === parentId) {
              onClearParent();
              return;
            }

            // Otherwise, select the new parent
            const selected = selectableParents.find((item) => item.model.uuid === parentId);
            if (selected) {
              onParentSelect(selected as ModelFlatNode<ModelDTO>);
            }
          }}
          options={selectableParents.map((item) => {
            const label =
              item.model.metadata?.therapeutic_area ||
              item.model.metadata?.disease ||
              getModelName(item.model) ||
              item.model.metadata?.name ||
              'Parent Entry';
            const version = item.model.metadata?.version_release_of_model;
            const displayLabel = version ? `${label} | ${version}` : label;
            return {
              label: displayLabel,
              value: item.model.uuid,
            };
          })}
        />

        {selectedParent && (
          <Card size="small" className={styles.parentPreviewCard}>
            <div className={styles.parentPreviewContent}>
              <NodeIndexOutlined className={styles.parentPreviewIcon} />
              <div className={styles.parentPreviewText}>
                <Text className={styles.parentPreviewMeta}>
                  {[selectedParent.model.metadata?.type, selectedParent.model.metadata?.therapeutic_area]
                    .filter(Boolean)
                    .join(' | ')}
                </Text>
                <Text className={styles.parentPreviewName}>{getModelName(selectedParent.model) || '-'}</Text>
                <Text className={styles.parentPreviewBottom}>
                  {[selectedParent.model.metadata?.country, selectedParent.model.metadata?.version_release_of_model]
                    .filter(Boolean)
                    .join(' | ')}
                </Text>
              </div>
            </div>
          </Card>
        )}

        {submitClicked && !isModelInHierarchy && <Alert message="Model is not connected yet" type="error" showIcon />}

        {/* Create New Lineage Button - Available in parent mode */}
        <div style={{textAlign: 'center', marginTop: 16}}>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={onNewHierarchy}
            disabled={externalModel}
            {...tid('btn-new-lineage-parent')}
          >
            Create New Lineage
          </Button>
        </div>

        {/* Switch to Root Selection - Only show if roots actually exist */}
        {possibleParentsCount === 0 && !isModelInHierarchy && roots && roots.length > 0 && (
          <div style={{textAlign: 'center', marginTop: 12}}>
            <Button type="link" onClick={() => onModeChange('root')} {...tid('btn-switch-to-root')}>
              View Root Selection Options
            </Button>
          </div>
        )}
      </Space>
    </div>
  );

  // Render Root Selection Mode
  const renderRootSelection = () => (
    <div className={styles.rootSelection}>
      <Space direction="vertical" size={16} style={{width: '100%'}}>
        {/* Instruction Text */}
        <Alert
          message={
            roots?.length
              ? 'Based on metadata, the following ancestry possibilities were found. Please choose which one is valid for this model, or create new lineage.'
              : 'Based on metadata, no ancestry possibilities were found.'
          }
          type="info"
          icon={<InfoCircleOutlined />}
          showIcon
        />

        {!validMetadata && (
          <Alert
            message="If the metadata is not properly filled in, this will result in no ancestry possibilities. Please update all metadata to show valid options."
            type="warning"
            icon={<WarningOutlined />}
            showIcon
          />
        )}

        {/* Root Options */}
        {roots === null ? (
          <div style={{textAlign: 'center', padding: 40}}>
            <Spin size="large" tip="Loading lineage options..." />
          </div>
        ) : (
          <div className={styles.rootGrid}>
            {roots?.map((rootModel) => (
              <Card
                key={rootModel.uuid}
                hoverable
                className={styles.rootCard}
                actions={[
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => onRootSelect(rootModel.uuid)}
                    disabled={externalModel}
                    {...tid('btn-select', rootModel.uuid)}
                  >
                    Select
                  </Button>,
                ]}
              >
                <Space direction="vertical" size={8} style={{width: '100%'}}>
                  <Text strong style={{fontSize: '16px'}}>
                    {getModelName(rootModel)}
                  </Text>
                  <Text type="secondary">Root Model UUID: {rootModel.uuid}</Text>
                  {rootModel.metadata?.product && <Text type="secondary">Product: {rootModel.metadata?.product}</Text>}
                  {rootModel.metadata?.therapeutic_area && (
                    <Text type="secondary">Therapeutic Area: {rootModel.metadata?.therapeutic_area}</Text>
                  )}
                </Space>
              </Card>
            ))}
          </div>
        )}

        {roots !== null && roots.length === 0 && (
          <Alert
            message="No matching published lineage roots were found for this metadata."
            description="Only published models are suggested in lineage options."
            type="warning"
            showIcon
          />
        )}

        {/* New Lineage Button */}
        <div style={{textAlign: 'center', marginTop: 24}}>
          <Button
            type="primary"
            icon={<LinkOutlined />}
            onClick={onNewHierarchy}
            disabled={externalModel}
            size="large"
            {...tid('btn-new-lineage')}
          >
            Create New Lineage
          </Button>
        </div>

        {/* Back to Parent Selection */}
        <div style={{textAlign: 'center'}}>
          <Button type="link" onClick={() => onModeChange('parent')} {...tid('btn-back-to-parent')}>
            Back to Parent Selection
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <div className={styles.lineageSelectionAntd}>
      {currentMode === 'parent' ? renderParentSelection() : renderRootSelection()}
    </div>
  );
};

export default LineageSelectionAntd;
